import { Extension } from '@tiptap/core';
import { Fragment, Slice, Node as PMNode } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { closeHistory } from '@tiptap/pm/history';
import { FormFieldDto } from '../../general/dtos';
import { insideFieldContainer } from './field-container';

const types = new Set(['text', 'inline-text', 'textarea', 'number', 'email', 'select', 'radio', 'checkbox', 'checkbox-group', 'multi-select', 'date', 'quiz-list']);
const reference = (node: PMNode): string => node.attrs['field-key'] || node.attrs.key;
function mapFragment(fragment: Fragment, map: (node: PMNode) => PMNode): Fragment {
    const nodes: PMNode[] = [];
    fragment.forEach(node => nodes.push(map(node.isLeaf ? node : node.copy(mapFragment(node.content, map)))));
    return Fragment.fromArray(nodes);
}

/** Clipboard HTML is untrusted; accept field definitions, never executable content or references to other records. */
function normalizeDefinition(field: any, key: string): FormFieldDto {
    const isBooleanFlag = (value: unknown) => value === true || value === false || value === 0 || value === 1;
    if (!field || typeof field !== 'object' || Array.isArray(field)
        || field.key !== key || !types.has(field.type) || typeof field.label !== 'string' || !isBooleanFlag(field.required)
        || (field.is_label_hidden != null && !isBooleanFlag(field.is_label_hidden))
        || (field.options != null && (!Array.isArray(field.options) || field.options.some((option: any) =>
            !option || typeof option.key !== 'string' || typeof option.value !== 'string'))))
        throw new Error('Invalid copied field definition.');
    // MySQL JSON_OBJECT returns the stored bit flags as 0/1; unsaved dialog fields use booleans.
    return { ...field, required: Boolean(field.required),
        ...(field.is_label_hidden != null ? { is_label_hidden: Boolean(field.is_label_hidden) } : {}) };
}
function readDefinition(payload: string, key: string): FormFieldDto {
    const envelope = JSON.parse(payload);
    if (envelope?.version !== 1) throw new Error('Invalid copied field definition version.');
    return normalizeDefinition(envelope.field, key);
}

export const FormFieldClipboard = Extension.create({
    name: 'formFieldClipboard',
    priority: 1100, // Handle valid field-only pastes before the container's general paste guard.
    addOptions() {
        return {
            getField: (_key: string): FormFieldDto | null => null,
            addFields: (_fields: FormFieldDto[]): void => {},
            onError: (_message: string): void => {},
        };
    },
    addProseMirrorPlugins() {
        const editor = this.editor;
        const options = this.options;
        // A second copy/paste can happen before React commits the new definitions.
        const pending = new Map<string, FormFieldDto>();
        const getField = (key: string) => options.getField(key) ?? pending.get(key) ?? null;
        return [new Plugin({
            view: () => ({ update: () => {
                // Once React supplies a definition, stop retaining a fallback that could outlive its deletion.
                for (const key of pending.keys()) if (options.getField(key)) pending.delete(key);
            } }),
            props: {
            transformCopied(slice) {
                return new Slice(mapFragment(slice.content, node => {
                    if (node.type.name !== 'formField') return node;
                    const field = getField(reference(node));
                    return node.type.create({ ...node.attrs, clipboardField: field ? JSON.stringify({ version: 1, field }) : null }, node.content, node.marks);
                }), slice.openStart, slice.openEnd);
            },
            handlePaste(view, _event, slice) {
                if (!editor.isEditable) return false;
                let containsFields = false;
                slice.content.descendants(node => { if (node.type.name === 'formField') containsFields = true; });
                if (!containsFields) return false;
                const copies = new Map<string, FormFieldDto>();
                let remapped: Slice;
                try {
                    remapped = new Slice(mapFragment(slice.content, node => {
                        if (node.type.name !== 'formField') return node;
                        const key = reference(node);
                        if (!copies.has(key)) {
                            const source = node.attrs.clipboardField ? readDefinition(node.attrs.clipboardField, key) : normalizeDefinition(getField(key), key);
                            if (!source) throw new Error('Missing field definition.');
                            // Option keys are scoped to their field. Keep them, including default/expected answer references.
                            const copy: FormFieldDto = JSON.parse(JSON.stringify(source));
                            copy.key = crypto.randomUUID();
                            copies.set(key, copy);
                        }
                        const copy = copies.get(key)!;
                        return node.type.create({ ...node.attrs, clipboardField: null,
                            ...(node.attrs['field-key'] ? { 'field-key': copy.key } : { key: copy.key }) }, node.content, node.marks);
                    }), slice.openStart, slice.openEnd);
                } catch {
                    options.onError('These fields could not be pasted because their definitions are missing or invalid. Copy them again from the source editor.');
                    return true;
                }
                if (insideFieldContainer(editor)) {
                    const fields: PMNode[] = [];
                    let valid = true;
                    const visit = (node: PMNode) => {
                        if (node.type.name === 'formField') {
                            if (node.attrs['field-key'] || node.attrs['expected-key'] || [...copies.values()].some(f => f.key === node.attrs.key && f.type === 'quiz-list')) valid = false;
                            else fields.push(node.mark([]));
                        } else if (node.isTextblock) node.forEach(visit);
                        else if (node.type.name === 'hardBreak' || (node.isText && !node.text?.trim())) return;
                        else valid = false;
                    };
                    remapped.content.forEach(visit);
                    if (!valid || !fields.length) return true;
                    remapped = new Slice(Fragment.fromArray(fields), 0, 0);
                }
                const before = view.state.doc;
                const tr = closeHistory(view.state.tr).replaceSelection(remapped).setMeta('paste', true).setMeta('uiEvent', 'paste');
                view.dispatch(tr.scrollIntoView());
                if (view.state.doc !== before) {
                    // Only register definitions for nodes that survived schema fitting/transaction filters.
                    const inserted = new Set<string>();
                    view.state.doc.descendants(node => { if (node.type.name === 'formField') inserted.add(reference(node)); });
                    const fields = [...copies.values()].filter(field => inserted.has(field.key));
                    fields.forEach(field => pending.set(field.key, field));
                    options.addFields(fields);
                }
                return true;
            },
        } })];
    },
});
